import {
  catalogSeed,
  donationCampaignSeed,
  filterCatalog,
  impactSeed,
  merchSeed,
  type CatalogQuery,
  type Cohort,
  type Course,
  type CreateCohortInput,
  type CreateCourseInput,
  type DonationCampaign,
  type EnrollInput,
  type FundDonationInput,
  type ImpactSnapshot,
  type LoginInput,
  type MerchOrderInput,
  type MerchProduct,
  type PublicUser,
  type RegisterInput,
  type TechDonationInput,
  type UpdateCourseInput,
  type UpdateDonationCampaignInput,
  type UpdateImpactInput,
  type UpdateMerchProductInput,
} from '@juvo/schemas';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

export interface AuthSession {
  user: PublicUser;
  accessToken: string;
}

export interface CourseDetail {
  course: Course;
  cohorts: Cohort[];
}

function fallbackCourses(query: CatalogQuery = {}): Course[] {
  return filterCatalog(catalogSeed, query).map((course, index) => {
    const seed = catalogSeed.find((item) => item.slug === course.slug);
    const upcoming = seed?.upcoming;
    const { upcoming: _ignored, ...rest } = course;
    if (!upcoming) return { id: `seed-${index + 1}`, ...rest };
    const seatsTaken = Math.min(upcoming.capacity - 1, Math.round(upcoming.capacity * 0.65));
    return {
      id: `seed-${index + 1}`,
      ...rest,
      upcoming: {
        startDate: upcoming.startDate,
        endDate: upcoming.endDate,
        capacity: upcoming.capacity,
        seatsTaken,
        location: course.location,
      },
    };
  });
}

function fallbackCohorts(course: Course): Cohort[] {
  const seed = catalogSeed.find((item) => item.slug === course.slug);
  if (!seed) return [];
  return [
    {
      id: `seed-cohort-${course.slug}`,
      courseId: course.id,
      courseSlug: course.slug,
      startDate: seed.upcoming.startDate,
      endDate: seed.upcoming.endDate,
      capacity: seed.upcoming.capacity,
      seatsTaken: 0,
      location: course.location,
      status: 'open',
      language: seed.upcoming.language,
    },
  ];
}

export class JuvoClient {
  constructor(private readonly baseUrl: string) {}

  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    if (init.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    if (this.token) headers.set('Authorization', `Bearer ${this.token}`);

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      let details: unknown;
      try {
        details = await response.json();
      } catch {
        details = await response.text();
      }
      throw new ApiError(`Request failed (${response.status})`, response.status, details);
    }

    const ct = response.headers.get('content-type') || '';
    if (!ct.includes('json')) {
      throw new ApiError('Invalid JSON', response.status);
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  async getCourses(query: CatalogQuery = {}): Promise<Course[]> {
    const params = new URLSearchParams();
    if (query.q) params.set('q', query.q);
    if (query.track) params.set('track', query.track);
    if (query.level) params.set('level', query.level);
    if (query.programmeType) params.set('programmeType', query.programmeType);
    if (query.locale) params.set('locale', query.locale);
    const qs = params.toString();
    try {
      return await this.request<Course[]>(`/courses${qs ? `?${qs}` : ''}`);
    } catch {
      return fallbackCourses(query);
    }
  }

  async getCourse(slug: string): Promise<CourseDetail | null> {
    try {
      return await this.request<CourseDetail>(`/courses/${encodeURIComponent(slug)}`);
    } catch {
      const course = fallbackCourses().find((item) => item.slug === slug);
      if (!course) return null;
      return { course, cohorts: fallbackCohorts(course) };
    }
  }

  async getImpact(): Promise<ImpactSnapshot> {
    try {
      return await this.request<ImpactSnapshot>('/impact');
    } catch {
      return impactSeed;
    }
  }

  async getMerch(): Promise<MerchProduct[]> {
    try {
      return await this.request<MerchProduct[]>('/merch');
    } catch {
      return merchSeed;
    }
  }

  async getDonationCampaign(): Promise<DonationCampaign> {
    try {
      return await this.request<DonationCampaign>('/donate/campaign');
    } catch {
      return donationCampaignSeed;
    }
  }

  async submitTechDonation(input: TechDonationInput): Promise<{ id: string; status: string }> {
    try {
      return await this.request('/donate/tech', { method: 'POST', body: JSON.stringify(input) });
    } catch (error) {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) throw error;
      return { id: `local-tech-${Date.now()}`, status: 'queued' };
    }
  }

  async submitFundDonation(input: FundDonationInput): Promise<{ id: string; status: string }> {
    try {
      return await this.request('/donate/funds', { method: 'POST', body: JSON.stringify(input) });
    } catch (error) {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) throw error;
      return { id: `local-funds-${Date.now()}`, status: 'queued' };
    }
  }

  async submitMerchOrder(
    input: MerchOrderInput,
  ): Promise<{ id: string; status: string; totalUsd: number }> {
    try {
      return await this.request('/merch/orders', { method: 'POST', body: JSON.stringify(input) });
    } catch (error) {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) throw error;
      const catalog = merchSeed;
      const totalUsd = input.items.reduce((sum, item) => {
        const product = catalog.find((row) => row.sku === item.sku);
        return sum + (product ? product.priceUsd * item.qty : 0);
      }, 0);
      return { id: `local-order-${Date.now()}`, status: 'queued', totalUsd };
    }
  }

  async register(input: RegisterInput): Promise<AuthSession> {
    const session = await this.request<AuthSession>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    this.setToken(session.accessToken);
    return session;
  }

  async login(input: LoginInput): Promise<AuthSession> {
    const session = await this.request<AuthSession>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    this.setToken(session.accessToken);
    return session;
  }

  async magicLink(email: string): Promise<{ sent: boolean; devToken?: string }> {
    return this.request('/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async verifyMagic(token: string): Promise<AuthSession> {
    const session = await this.request<AuthSession>('/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    this.setToken(session.accessToken);
    return session;
  }

  async me(): Promise<PublicUser> {
    return this.request('/auth/me');
  }

  async logout(): Promise<void> {
    try {
      await this.request('/auth/logout', { method: 'POST' });
    } finally {
      this.setToken(null);
    }
  }

  async enroll(input: EnrollInput): Promise<{ id: string; status: string }> {
    return this.request('/enrollments', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async myEnrollments(): Promise<unknown[]> {
    return this.request('/enrollments/me');
  }

  async createCourse(input: CreateCourseInput): Promise<Course> {
    return this.request<Course>('/courses', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateCourse(idOrSlug: string, input: UpdateCourseInput): Promise<Course> {
    return this.request<Course>(`/courses/${encodeURIComponent(idOrSlug)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  async deleteCourse(idOrSlug: string): Promise<{ success: boolean; deletedSlug: string }> {
    return this.request<{ success: boolean; deletedSlug: string }>(
      `/courses/${encodeURIComponent(idOrSlug)}`,
      { method: 'DELETE' },
    );
  }

  async createCohort(courseId: string, input: CreateCohortInput): Promise<Cohort> {
    return this.request<Cohort>(`/courses/${encodeURIComponent(courseId)}/cohorts`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async createMerchProduct(input: MerchProduct): Promise<MerchProduct> {
    return this.request<MerchProduct>('/merch', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateMerchProduct(sku: string, input: UpdateMerchProductInput): Promise<MerchProduct> {
    return this.request<MerchProduct>(`/merch/${encodeURIComponent(sku)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  async deleteMerchProduct(sku: string): Promise<{ success: boolean; deletedSku: string }> {
    return this.request<{ success: boolean; deletedSku: string }>(
      `/merch/${encodeURIComponent(sku)}`,
      { method: 'DELETE' },
    );
  }

  async updateImpact(input: UpdateImpactInput): Promise<ImpactSnapshot> {
    return this.request<ImpactSnapshot>('/impact', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  async updateCampaign(input: UpdateDonationCampaignInput): Promise<DonationCampaign> {
    return this.request<DonationCampaign>('/donate/campaign', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }
}

export function createClient(baseUrl = '/api') {
  return new JuvoClient(baseUrl.replace(/\/$/, ''));
}
